/**
 * Accordion to have fold out content
 * Should have children of AccordionItem
 */

import { CONFIG } from '@bulknutrients/bulk-shared-lib/constants';
import { mergeStyles } from '@bulknutrients/bulk-shared-lib/utils';
import { Icons } from '@components/Icons/Icons';
import { type ReactElement, type ReactNode, useRef } from 'react';

const BASE_ITEM_STYLE =
  'bg-transparent group flex w-full cursor-pointer items-center justify-between p-0 text-black dark:text-white';
const STYLES = {
  default: `${BASE_ITEM_STYLE} py-2 px-0 clickable text-lg no-underline border-grey-mid border-solid border-0 border-b hover:border-orange active:border-orange`,
  pill: `${BASE_ITEM_STYLE} accordion-item py-2 px-4 mb-1 border text-xl rounded-3xl border-black border-2 hover:border-orange active:border-orange dark:border-white`,
};

interface AccordionItemProps {
  title: string | ReactNode;
  content: ReactNode;
  id?: string;
  className?: string;
  props?: Record<string, any>;
}

function Item({
  item,
  variant,
  defaultOpen = false,
}: {
  item: AccordionItemProps;
  variant: string;
  defaultOpen?: boolean;
}): ReactElement {
  const summaryRef = useRef<HTMLElement>(null);
  return (
    <details
      open={defaultOpen}
      className={
        variant === 'default'
          ? 'open:[&>summary]:border-orange open:[&>summary]:border-b-3 open:[&>summary>.chev]:rotate-180'
          : 'open:[&>summary]:border-orange open:[&>summary]:font-bold open:[&>*:not(summary)]:px-2 open:[&>summary>.chev]:rotate-180'
      }
      onToggle={() => {
        if (summaryRef.current) {
          const isOpen = summaryRef.current.parentElement?.hasAttribute('open');
          summaryRef.current.dataset.umamiEventAction = isOpen
            ? 'close'
            : 'open';
        }
      }}
    >
      <summary
        ref={summaryRef}
        className={mergeStyles(STYLES[variant], item.className ?? '')}
        id={item.id}
        aria-label={
          typeof item.title === 'string' ? item.title : item.title?.toString()
        }
        data-umami-event="accordion-interaction"
        data-umami-event-item={item.title}
        data-umami-event-id={item.id}
        data-umami-event-action={defaultOpen ? 'close' : 'open'}
        {...item.props}
      >
        {item.title}
        <Icons.Chevron.Down
          size={CONFIG.defaultIconSize}
          className="chev min-w-5"
        />
      </summary>
      {item.content}
    </details>
  );
}

export function Accordion({
  items,
  defaultOpen = false,
  openIndexes = [],
  variant = 'default',
}: {
  items: {
    title: string | ReactNode;
    content: ReactNode;
    id?: string;
    props?: Record<string, any>;
  }[];
  defaultOpen?: boolean;
  openIndexes?: (string | number)[];
  variant?: 'default' | 'pill';
}): ReactElement {
  return (
    <>
      {items.map((item, index) => (
        <Item
          key={index}
          item={item}
          variant={variant}
          defaultOpen={
            defaultOpen ||
            openIndexes.includes(index) ||
            openIndexes.includes(item.id ?? '')
          }
        />
      ))}
    </>
  );
}
